/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2025.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {Alert} from "@mui/material";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import Modal from "@mui/material/Modal";
import {createTheme, ThemeProvider} from "@mui/material/styles";
import {QController} from "@temple-of-syrinx/qqq-frontend-core/lib/controllers/QController";
import {QFieldMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QFieldMetaData";
import {QTableMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableMetaData";
import {QWidgetMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QWidgetMetaData";
import {QRecord} from "@temple-of-syrinx/qqq-frontend-core/lib/model/QRecord";
import {Formik, FormikErrors, FormikProps} from "formik";
import QContext from "QContext";
import DateTimeBadInputTracker from "qqq/components/forms/DateTimeBadInputTracker";
import QDynamicForm from "qqq/components/forms/DynamicForm";
import DynamicFormUtils from "qqq/components/forms/DynamicFormUtils";
import {preferredColorNameInfoOrPrimary} from "qqq/assets/theme/functions/preferInfoColorToPrimaryColor";
import MDButton from "qqq/components/legacy/MDButton";
import staticLegacyTheme from "qqq/components/legacy/Theme";
import DashboardWidgets from "qqq/components/widgets/DashboardWidgets";
import {MaterialUIControllerProvider} from "qqq/context";
import Client from "qqq/utils/qqq/Client";
import React, {ReactElement, ReactNode, useContext, useEffect, useMemo, useRef, useState} from "react";
import {BrowserRouter, MemoryRouter, useInRouterContext} from "react-router-dom";
import * as Yup from "yup";

//////////////////////////////
// Context value type alias //
//////////////////////////////
type QContextType = React.ContextType<typeof QContext>;


/***************************************************************************
 ** Build the MUI theme that bridge-rendered content should use.
 **
 ** Bridge content is frequently rendered for dynamically-loaded components
 ** (loaded as separate UMD bundles) and inside MUI Modal portals.  We can
 ** NOT rely on React/MUI ThemeProvider context reaching here - so a
 ** context-based approach (useTheme) silently falls back to the default
 ** pink palette.
 **
 ** The host injects the deployment's palette as CSS custom properties on
 ** document.documentElement (see injectIslandVariables - --qqq-primary-color
 ** etc.).  Those are global to the document and therefore DO reach bridge
 ** content regardless of bundle or portal boundaries.  So we read them and
 ** build a theme off the static legacy theme, overriding the palette with
 ** the live values.  When the vars are absent (a non-themed deployment) we
 ** keep the static legacy theme so the default look is preserved.
 ***************************************************************************/
function readCssVar(name: string): string | null
{
   if (typeof window === "undefined" || !document?.documentElement)
   {
      return null;
   }
   const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
   return value || null;
}

function useBridgeTheme()
{
   return useMemo(() =>
   {
      /////////////////////////////////////////////////////////////////////////////
      // LONG-TERM: this reconstructs the theme from CSS vars because the host's //
      // real (dynamic) theme can't be relied on to cross the dynamic-component  //
      // UMD-bundle / Modal-portal boundary.  If more theming gaps show up here, //
      // the generic fix is to have the host publish its actual dynamicTheme     //
      // somewhere bundle-reachable and consume that instead of re-deriving it.  //
      /////////////////////////////////////////////////////////////////////////////
      const paletteVarMap: [string, string][] = [
         ["primary", "--qqq-primary-color"],
         ["secondary", "--qqq-secondary-color"],
         ["info", "--qqq-info-color"],
         ["success", "--qqq-success-color"],
         ["warning", "--qqq-warning-color"],
         ["error", "--qqq-error-color"],
      ];

      /////////////////////////////////////////////////////////////////////////////////
      // mirror createDynamicTheme: only the primary & info *gradients* are themed   //
      // (MDButton variant="gradient" reads palette.gradients, not palette.primary). //
      // leaving the rest as the static defaults keeps gradient buttons looking the  //
      // same here as they do everywhere else in the app.                            //
      /////////////////////////////////////////////////////////////////////////////////
      const gradientKeys = new Set(["primary", "info"]);

      const paletteOverride: { [key: string]: any } = {};
      const gradients: { [key: string]: any } = {};
      for (const [paletteKey, varName] of paletteVarMap)
      {
         const value = readCssVar(varName);
         if (!value)
         {
            continue;
         }

         ////////////////////////////////////////////////////////////////////////////////
         // real color lets MUI derive contrastText/light/dark; keep qqq's "focus" key //
         ////////////////////////////////////////////////////////////////////////////////
         paletteOverride[paletteKey] = {main: value, focus: value};

         if (gradientKeys.has(paletteKey))
         {
            gradients[paletteKey] = {main: `color-mix(in srgb, ${value} 65%, white)`, state: value};
         }
      }

      if (Object.keys(paletteOverride).length === 0)
      {
         return staticLegacyTheme;
      }

      paletteOverride.gradients = gradients;

      return createTheme(staticLegacyTheme, {palette: paletteOverride});
   }, []);
}


// todo - deploy this interface somehow out of this file
export interface QFMDBridge
{
   qController?: QController;
   makeAlert: (text: string, color: string) => JSX.Element;
   makeButton: (label: string, onClick: () => void, extra?: { [key: string]: any }) => JSX.Element;
   makeForm: (fields: QFieldMetaData[], record: QRecord, handleChange: (fieldName: string, newValue: any) => void, handleSubmit: (values: any) => void, helpRoles?: string[], helpContentKeyPrefix?: string, qContext?: QContextType) => JSX.Element;
   makeModal: (children: ReactElement, onClose?: (setIsOpen: (isOpen: boolean) => void, event: {}, reason: "backdropClick" | "escapeKeyDown") => void) => JSX.Element;
   makeWidget: (widgetName: string, tableName?: string, entityPrimaryKey?: string, record?: QRecord, actionCallback?: (data: any, eventValues?: { [name: string]: any }) => boolean) => JSX.Element;
}


/***************************************************************************
 ** Component to generate a form for the QFMD Bridge
 ***************************************************************************/
interface QFMDBridgeFormProps
{
   fields: QFieldMetaData[],
   record: QRecord,
   handleChange: (fieldName: string, newValue: any) => void,
   handleSubmit: (values: any) => void,
   helpRoles?: string[],
   helpContentKeyPrefix?: string,

   ///////////////////////////////////////////////////////////////////////////////////
   // the host app's QContext, as makeModal and makeWidget also accept.  A bridged  //
   // form renders in its own React root, so it does not inherit the host's context //
   // provider - useContext(QContext) there returns the DEFAULTS, whose accentColor //
   // is a blue that has nothing to do with the app's branding.  That shows up on   //
   // anything the form opens which reads the context: a possible-value dropdown's  //
   // Search screen draws its quick-filter 'x' and lock icons from accentColor.     //
   ///////////////////////////////////////////////////////////////////////////////////
   qContext?: QContextType,
}

QFMDBridgeForm.defaultProps = {};

////////////////////////////////////////////////////////////////////////////////////
// Bridge helper: is helpHelp on, per the URL?  The context half of that question //
// is answered by the caller, which knows whether the host handed one in - this   //
// hook can only see the AMBIENT context, and reading that here would overwrite a //
// passed-in context's helpHelpActive with the default false.                     //
////////////////////////////////////////////////////////////////////////////////////
const useBridgeHelpHelpActiveFromUrl = (): boolean =>
{
   return ((typeof window !== "undefined") && new URLSearchParams(window.location.search).has("helpHelp"));
};

function QFMDBridgeForm({fields, record, handleChange, handleSubmit, helpRoles, helpContentKeyPrefix, qContext: qContextProp}: QFMDBridgeFormProps): JSX.Element
{
   ///////////////////////////////////////////////////////////////////////////////////
   // prefer the host's context when it was handed to us - see the prop's comment.  //
   // the ambient one is still read (a hook cannot be called conditionally), and is //
   // the right answer when this form IS inside the host's provider.                //
   ///////////////////////////////////////////////////////////////////////////////////
   const ambientContext = useContext(QContext) as QContextType;
   const parentContext = qContextProp ?? ambientContext;

   ////////////////////////////////////////////////////////////////////////////////
   // read helpHelp off the context we actually settled on, not off the ambient  //
   // one - a host that passes its context while help-help is on would otherwise //
   // have it turned back off here, since the ambient context is the defaults.   //
   ////////////////////////////////////////////////////////////////////////////////
   const helpHelpActiveFromUrl = useBridgeHelpHelpActiveFromUrl();
   const helpHelpActive        = Boolean(parentContext?.helpHelpActive || helpHelpActiveFromUrl);
   const mergedContext: QContextType = {...parentContext, helpHelpActive};
   const bridgeTheme = useBridgeTheme();

   ////////////////////////////////////////////////////////////////////////////////////
   // does this form already have react-router context?  see the return, below.      //
   // this hook answers the question without throwing, unlike useLocation & friends. //
   ////////////////////////////////////////////////////////////////////////////////////
   const inRouterContext = useInRouterContext();

   ///////////////////////////////////////////////////////////////////////////////
   // date-time fields whose typed date doesn't exist (e.g. Feb 31) - see the   //
   // tracker.  a field the host hands back holding the marker (it redraws this //
   // form after a refused save) starts out empty, already showing the error.   //
   ///////////////////////////////////////////////////////////////////////////////
   const [dateTimeBadInputTracker] = useState(() => new DateTimeBadInputTracker(fields, record.values));
   const formikRef = useRef<FormikProps<any>>(null);

   const initialValues: any = {};
   const initialErrors: FormikErrors<any> = {};
   const initialTouched: { [fieldName: string]: boolean } = {};
   for (let field of fields)
   {
      initialValues[field.name] = record.values.get(field.name);
      if (dateTimeBadInputTracker.isBad(field.name))
      {
         initialValues[field.name] = "";
         initialErrors[field.name] = "Invalid date";
         initialTouched[field.name] = true;
      }
      if (initialValues[field.name] === undefined && field.defaultValue !== undefined)
      {
         initialValues[field.name] = field.defaultValue;
      }

   }
   const [lastValues, setLastValues] = useState(initialValues);
   const [loaded, setLoaded] = useState(false);

   ///////////////////////////////////////////////////////////////////////////////
   // store reference to record display values in a state var - see usage below //
   ///////////////////////////////////////////////////////////////////////////////
   const [recordDisplayValues, setRecordDisplayValues] = useState(record?.displayValues ?? new Map<string, string>());

   const checkDateTimeInput = (event: React.SyntheticEvent) =>
   {
      const change = dateTimeBadInputTracker.onInputEvent(event.target);
      if (change)
      {
         handleChange(change.fieldName, change.value);
         formikRef.current?.validateForm();
      }

      ////////////////////////////////////////////////////////////////////////////////
      // a half-typed date is bad input too, so only show the error once the user   //
      // leaves the field (the host is told right away, so it still can't be saved) //
      ////////////////////////////////////////////////////////////////////////////////
      const fieldName = (event.target as HTMLInputElement).name;
      if (event.type === "blur" && dateTimeBadInputTracker.isBad(fieldName))
      {
         formikRef.current?.setFieldTouched(fieldName, true);
      }
   };

   const validateDateTimeInputs = (): FormikErrors<any> =>
   {
      const errors: FormikErrors<any> = {};
      dateTimeBadInputTracker.getBadFieldNames().forEach(fieldName => errors[fieldName] = "Invalid date");
      return (errors);
   };

   useEffect(() =>
   {
      (async () =>
      {
         const qController = Client.getInstance();

         for (let field of fields)
         {
            const value = record.values.get(field.name);
            if (field.possibleValueSourceName && value)
            {
               const {results: possibleValues} = await qController.possibleValues(null, null, field.possibleValueSourceName, null, [value], [], record.values, "form");
               if (possibleValues && possibleValues.length > 0)
               {
                  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
                  // originally, we put this in record.displayValues, but, sometimes that would then be empty at the usage point below... //
                  // this works, so, we'll go with it                                                                                     //
                  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
                  recordDisplayValues.set(field.name, possibleValues[0].label);
                  setRecordDisplayValues(recordDisplayValues);
               }
            }
         }

         setLoaded(true);
      })();
   }, []);

   if (!loaded)
   {
      return (<Box py={"1rem"}>Loading...</Box>);
   }

   const {
      dynamicFormFields,
      formValidations,
   } = DynamicFormUtils.getFormData(fields);
   DynamicFormUtils.addPossibleValueProps(dynamicFormFields, fields, null, null, recordDisplayValues);

   const otherValuesMap = new Map<string, any>();
   record.values.forEach((value, key) => otherValuesMap.set(key, value));

   for (let fieldName in dynamicFormFields)
   {
      const dynamicFormField = dynamicFormFields[fieldName];
      if (dynamicFormField.possibleValueProps)
      {
         dynamicFormField.possibleValueProps.otherValues = otherValuesMap;
      }
   }

   /////////////////////////////////////////////////////////////////////////////////
   // re-introduce these two context providers, in case the child calls this      //
   // method under a different root... maybe this should be optional per a param? //
   //                                                                             //
   // MemoryRouter: a form field can open components that use react-router hooks  //
   // (a possible-value dropdown's "Add new" / "Search" mount RecordScreen and    //
   // RecordQuery, which call useSearchParams / useNavigate / useLocation).  This //
   // form renders in its own React root, so it does not inherit the host app's   //
   // Router and those hooks would throw.                                         //
   //                                                                             //
   // Deliberately a MemoryRouter, not the BrowserRouter that QFMDBridgeWidget    //
   // uses: an embedded form must not be able to navigate the host page, and its  //
   // children must not read the host URL's query string (a picker reading the    //
   // host's search params would silently inherit an unrelated filter).           //
   /////////////////////////////////////////////////////////////////////////////////
   const formBody = (
      <QContext.Provider value={mergedContext}>
         <MaterialUIControllerProvider>
            <ThemeProvider theme={bridgeTheme}>
               <Formik innerRef={formikRef} initialValues={initialValues} initialErrors={initialErrors} initialTouched={initialTouched} validationSchema={Yup.object().shape(formValidations)} validate={validateDateTimeInputs} onSubmit={handleSubmit}>
                  {({values, errors, touched}) =>
                  {
                     const formData: any = {};
                     formData.values = values;
                     formData.touched = touched;
                     formData.errors = errors;
                     formData.formFields = dynamicFormFields;

                     try
                     {
                        let anyDiffs = false;
                        for (let fieldName in values)
                        {
                           const value = values[fieldName];
                           if (lastValues[fieldName] != value)
                           {
                              //////////////////////////////////////////////////////////////////////////
                              // a bad date's value is the browser's empty string - the host has been //
                              // told INVALID_DATE_TIME_VALUE instead, so don't overwrite that here   //
                              //////////////////////////////////////////////////////////////////////////
                              if (!dateTimeBadInputTracker.isBad(fieldName))
                              {
                                 handleChange(fieldName, value);
                              }
                              lastValues[fieldName] = value;
                              anyDiffs = true;
                           }
                        }

                        if (anyDiffs)
                        {
                           setLastValues(lastValues);
                        }
                     }
                     catch (e)
                     {
                        console.error(e);
                     }

                     ////////////////////////////////////////////////////////////////////////////
                     // a bad date fires no change (the value stays empty), so check the input //
                     // on every keystroke and when it loses focus, as well as on input.       //
                     ////////////////////////////////////////////////////////////////////////////
                     return (
                        <div onInput={checkDateTimeInput} onKeyUp={checkDateTimeInput} onBlur={checkDateTimeInput}>
                           <QDynamicForm formData={formData} record={record} helpRoles={helpRoles} helpContentKeyPrefix={helpContentKeyPrefix} />
                        </div>);
                  }}
               </Formik>
            </ThemeProvider>
         </MaterialUIControllerProvider>
      </QContext.Provider>);

   ///////////////////////////////////////////////////////////////////////////////////
   // supply a Router ONLY when there isn't one already.  this form is used both in //
   // its own React root (a custom-component island, where there is no Router and   //
   // these hooks would throw) and within the app tree (where there is one, and a   //
   // second Router is itself a hard error).                                        //
   ///////////////////////////////////////////////////////////////////////////////////
   return (inRouterContext ? formBody : <MemoryRouter>{formBody}</MemoryRouter>);
}


/***************************************************************************
 ** Component to render a widget for the QFMD Bridge
 ***************************************************************************/
interface QFMDBridgeWidgetProps
{
   widgetName?: string,
   tableName?: string,
   record?: QRecord,
   entityPrimaryKey?: string,
   actionCallback?: (data: any, eventValues?: { [p: string]: any }) => boolean,
   qContext?: QContext,
}

QFMDBridgeWidget.defaultProps = {};

function QFMDBridgeWidget({widgetName, tableName, record, entityPrimaryKey, actionCallback, qContext: qContextProp}: QFMDBridgeWidgetProps): JSX.Element
{
   const qContext = useContext(QContext) as QContextType;
   const bridgeTheme = useBridgeTheme();

   //////////////////////////////////////////////////////////////////////
   // see the return - whether we need to supply react-router context. //
   //////////////////////////////////////////////////////////////////////
   const inRouterContext = useInRouterContext();

   const [ready, setReady] = useState(false);

   const [widgetMetaData, setWidgetMetaData] = useState(null as QWidgetMetaData);
   const [widgetData, setWidgetData] = useState(null as any);

   const [tableMetaData, setTableMetaData] = useState(null as QTableMetaData);

   useEffect(() =>
   {
      (async () =>
      {
         const qController = Client.getInstance();
         const qInstance = await qController.loadMetaData();

         const formData: FormData = new FormData();
         for (let key of record?.values?.keys())
         {
            formData.append(key, record.values.get(key));
         }

         setWidgetMetaData(qInstance.widgets.get(widgetName));
         setWidgetData(await qController.widget(widgetName, formData));

         setReady(true);
      })();
   }, []);

   if (!ready)
   {
      return (<Box py={"1rem"}>Loading...</Box>);
   }

   ///////////////////////////////////////////////////////////////////////////////////////////////////////////
   // internally in some widgets, useNavigate happens... so we must re-introduce the browser-router context //
   // plus the contexts too, as indicated.                                                                  //
   ///////////////////////////////////////////////////////////////////////////////////////////////////////////
   const widgetBody = (
      <MaterialUIControllerProvider>
         <ThemeProvider theme={bridgeTheme}>
            <QContext.Provider value={{
               ...(qContextProp ?? qContext),
               setTableMetaData: (tableMetaData: QTableMetaData) => setTableMetaData(tableMetaData),
            }}>
               <div className={`bridgedWidget ${widgetMetaData.type}`}>
                  <DashboardWidgets tableName={tableName} widgetMetaDataList={[widgetMetaData]} initialWidgetDataList={[widgetData]} record={record} entityPrimaryKey={entityPrimaryKey} omitWrappingGridContainer={true} actionCallback={actionCallback} />
               </div>
            </QContext.Provider>
         </ThemeProvider>
      </MaterialUIControllerProvider>);

   ////////////////////////////////////////////////////////////////////////////////////
   // supply a Router only when there isn't one already - rendering a Router inside  //
   // another Router is a hard error, so an unconditional one here would break this  //
   // widget anywhere it is used within the app tree rather than its own React root. //
   ////////////////////////////////////////////////////////////////////////////////////
   return (inRouterContext ? widgetBody : <BrowserRouter>{widgetBody}</BrowserRouter>);
}


/***************************************************************************
 ** Component to render a modal for the QFMD Bridge
 ***************************************************************************/
interface QFMDBridgeModalProps
{
   children: ReactNode,
   onClose?: (setIsOpen: (isOpen: boolean) => void, event: {}, reason: "backdropClick" | "escapeKeyDown") => void,
   qContext?: QContextType,
   modalIdentifier?: string,
}

QFMDBridgeModal.defaultProps = {};

function QFMDBridgeModal({children, onClose, qContext: qContextProp, modalIdentifier: modalIdentifierProp}: QFMDBridgeModalProps): JSX.Element
{
   const [isOpen, setIsOpen] = useState(true);
   const [modalIdentifier, setModalIdentifier] = useState(modalIdentifierProp ?? ("anonymousModal:" + (new Date().getTime())));
   const bridgeTheme = useBridgeTheme();

   //////////////////////////////////////////////////////////////////////////////////
   // tie modalStack push/pop to mount/unmount so callers who toggle the modal off  //
   // by simply un-rendering it (e.g., `{open && makeModal(...)}`) don't strand the //
   // stack entry — which would otherwise keep RecordView/RecordQuery hotkeys gated //
   // by `modalStack.length === 0` from firing                                      //
   //////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      qContextProp?.pushModalOnStack?.(modalIdentifier);
      return () => qContextProp?.popModalOffStack?.(modalIdentifier);
   }, []);


   function doSetIsOpen(isOpen: boolean): void
   {
      setIsOpen(isOpen);
   }

   function closeModal(event: {}, reason: "backdropClick" | "escapeKeyDown")
   {
      if (onClose)
      {
         onClose(doSetIsOpen, event, reason);
      }
      else
      {
         doSetIsOpen(false);
      }
   }

   return (
      <Modal open={isOpen} onClose={(event, reason) => closeModal(event, reason)}>
         <Box className="bridgeModal" height="calc(100vh)">
            <ThemeProvider theme={bridgeTheme}>
               {children}
            </ThemeProvider>
         </Box>
      </Modal>
   );
}


/***************************************************************************
 ** Component to render an alert for the QFMD Bridge
 ***************************************************************************/
interface QFMDBridgeAlertProps
{
   color: string,
   children: ReactNode,
   mayManuallyClose?: boolean
}

QFMDBridgeAlert.defaultProps = {};

function QFMDBridgeAlert({color, children, mayManuallyClose}: QFMDBridgeAlertProps): JSX.Element
{
   const [isOpen, setIsOpen] = useState(true);

   function onClose()
   {
      setIsOpen(false);
   }

   if (isOpen)
   {
      //@ts-ignore color
      return (<Alert color={color} onClose={mayManuallyClose ? onClose : null}>{children}</Alert>);
   }
   else
   {
      return (<React.Fragment />);
   }
}


/***************************************************************************
 ** Component to render a button for the QFMD Bridge
 ***************************************************************************/
interface QFMDBridgeButtonProps
{
   label: string,
   onClick: () => void,
   extra?: { [key: string]: any }
}

QFMDBridgeButton.defaultProps = {};

function QFMDBridgeButton({label, onClick, extra}: QFMDBridgeButtonProps): JSX.Element
{
   const bridgeTheme = useBridgeTheme();

   ////////////////////////////////////////////////////////////////////////////////////
   // dynamic components historically asked for color:"info" on affirmative action   //
   // buttons (Save/OK/etc) - the documented "should have been primary" mistake (see //
   // preferInfoColorToPrimaryColor).  Route "info" through the preferred-color       //
   // mechanism so it follows the deployment's choice (e.g. CTL's teal primary)       //
   // instead of rendering as that deployment's literal info color (CTL: gray).       //
   ////////////////////////////////////////////////////////////////////////////////////
   const resolvedExtra = (extra?.color === "info")
      ? {...extra, color: preferredColorNameInfoOrPrimary()}
      : extra;

   return (
      <MaterialUIControllerProvider>
         <ThemeProvider theme={bridgeTheme}>
            <MDButton {...resolvedExtra} onClick={onClick} fullWidth>{label}</MDButton>
         </ThemeProvider>
      </MaterialUIControllerProvider>
   );
}


/***************************************************************************
 ** define the default qfmd bridge object
 ***************************************************************************/
export const qfmdBridge =
   {
      qController: Client.getInstance(),

      makeIcon: (name: string): JSX.Element =>
      {
         return (<Icon>{name}</Icon>);
      },

      makeButton: (label: string, onClick: () => void, extra?: { [key: string]: any }): JSX.Element =>
      {
         return (<QFMDBridgeButton extra={extra} onClick={onClick} label={label} />);
      },

      makeAlert: (text: string, color: string, mayManuallyClose?: boolean): JSX.Element =>
      {
         const content = (<div>{text.split("\n").map((line, index) => (<div key={index}>{line}</div>))}</div>);
         return (<QFMDBridgeAlert color={color} mayManuallyClose={mayManuallyClose}>{content}</QFMDBridgeAlert>);
      },

      makeModal: (children: ReactElement, onClose?: (setIsOpen: (isOpen: boolean) => void, event: {}, reason: "backdropClick" | "escapeKeyDown") => void, qContext?: QContextType, modalIdentifier?: string): JSX.Element =>
      {
         return (<QFMDBridgeModal onClose={onClose} qContext={qContext} modalIdentifier={modalIdentifier}>{children}</QFMDBridgeModal>);
      },

      makeWidget: (widgetName: string, tableName?: string, entityPrimaryKey?: string, record?: QRecord, actionCallback?: (data: any, eventValues?: { [name: string]: any }) => boolean, qContext?: QContextType): JSX.Element =>
      {
         return (<QFMDBridgeWidget widgetName={widgetName} tableName={tableName} record={record} entityPrimaryKey={entityPrimaryKey} actionCallback={actionCallback} qContext={qContext} />);
      },

      makeForm: (fields: QFieldMetaData[], record: QRecord, handleChange: (fieldName: string, newValue: any) => void, handleSubmit: (values: any) => void, helpRoles?: string[], helpContentKeyPrefix?: string, qContext?: QContextType): JSX.Element =>
      {
         return (<QFMDBridgeForm fields={fields} record={record} handleChange={handleChange} handleSubmit={handleSubmit} helpRoles={helpRoles} helpContentKeyPrefix={helpContentKeyPrefix} qContext={qContext} />);
      }
   };

